import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore, isCrmManager } from '../store/authStore';
import { authService } from '../services/auth.service';
import { AccessPending } from '../pages/AccessPending';

interface ProtectedRouteProps {
  requireAdmin?: boolean;
  // Gate route(s) behind a manager-tier CRM role (manager / ceo /
  // client_manager). Mirrors the Rails CrmManagerGate so reps can't deep-link
  // into planning pages they couldn't write to anyway.
  requireCrmManager?: boolean;
}

export const ProtectedRoute = ({
  requireAdmin = false,
  requireCrmManager = false,
}: ProtectedRouteProps) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  const location = useLocation();

  // Check if localStorage and store state are in sync on every route change
  useEffect(() => {
    const token = authService.getToken();

    // If we think we're authenticated but localStorage has no token (e.g., after logout)
    if (isAuthenticated && !token) {
      useAuthStore.setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, [location.pathname, isAuthenticated]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // First-time Microsoft users wait here until an admin grants access (ENG-746).
  if (user?.access_pending) {
    return <AccessPending />;
  }

  if (requireAdmin && user?.role !== 'account_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireCrmManager && !isCrmManager(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
